/**
 * PasskeyLoginButton.jsx — drop-in below any login form.
 *
 * Asks for the email (so the server knows whose passkeys to load), kicks off
 * the WebAuthn assertion ceremony, then on success redirects via the
 * caller-provided `onSuccess` callback (for role-specific routing).
 */
import React, { useState } from 'react';
import { KeyRound } from 'lucide-react';
import { loginWithPasskey, isPasskeySupported } from '../utils/passkeyApi';
import { Button } from './ui/button';
import { Input } from './ui/input';

const PasskeyLoginButton = ({ defaultEmail = '', onSuccess, testId = 'passkey-login-btn' }) => {
  const [email, setEmail] = useState(defaultEmail);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showEmail, setShowEmail] = useState(false);

  if (!isPasskeySupported()) return null;

  const handleClick = async () => {
    setError('');
    if (!email) {
      setShowEmail(true);
      return;
    }
    setBusy(true);
    try {
      const user = await loginWithPasskey(email);
      onSuccess?.(user);
    } catch (e) {
      setError(e.message || 'Passkey sign-in failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={`${testId}-wrap`}>
      {showEmail && !defaultEmail && (
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your email"
          className="bg-slate-900/50 border-slate-700 text-white text-sm"
          data-testid={`${testId}-email`}
        />
      )}
      <Button
        type="button"
        onClick={handleClick}
        disabled={busy}
        variant="outline"
        className="w-full text-fuchsia-300 border-fuchsia-500/40 hover:bg-fuchsia-600/10 disabled:opacity-60"
        data-testid={testId}
      >
        <KeyRound className="w-4 h-4 mr-2" />
        {busy ? 'Authenticating...' : 'Sign in with passkey'}
      </Button>
      {error && (
        <p className="text-xs text-red-300 text-center" data-testid={`${testId}-error`}>{error}</p>
      )}
    </div>
  );
};

export default PasskeyLoginButton;
